import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DocumentTypeApi, MetadataDefinitionApi } from '../../services/ApiService';

function AssignFieldsToDocumentType() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [docType, setDocType] = useState(null);
  const [allFields, setAllFields] = useState([]);
  const [assignedRelationships, setAssignedRelationships] = useState([]); // Store full relationship objects
  const [fieldStates, setFieldStates] = useState({}); // Track assigned/required state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        // 1) Load DocumentType
        const docTypeResponse = await DocumentTypeApi.getDocumentType(id);
        if (!docTypeResponse.ok) {
          throw new Error(`Failed to fetch document type: ${docTypeResponse.status} ${docTypeResponse.statusText}`);
        }
        const docTypeData = await docTypeResponse.json();
        setDocType(docTypeData);

        // 2) Load all document metadata definitions
        const fieldsResponse = await MetadataDefinitionApi.getDocumentMetadataDefinitions();
        if (fieldsResponse.ok) {
          const fieldsData = await fieldsResponse.json();
          setAllFields(Array.isArray(fieldsData) ? fieldsData : []);
        } else {
          console.warn("Failed to fetch document metadata definitions:", fieldsResponse.status);
          setAllFields([]);
        }

        // 3) Load assigned metadata definitions (relationships) for this document type
        try {
          const assignedResponse = await DocumentTypeApi.getDocumentTypeMetadataDefinitions(id);
          if (assignedResponse.ok) {
            const assignedData = await assignedResponse.json();
            setAssignedRelationships(Array.isArray(assignedData) ? assignedData : []);
            
            // Initialize fieldStates based on assigned relationships
            const initialStates = {};
            assignedData.forEach(relationship => {
              initialStates[relationship.metadataDefinitionId] = {
                assigned: true,
                required: relationship.isRequired || false,
                relationshipId: relationship.id // Store the relationship ID for updates/deletes
              };
            });
            setFieldStates(initialStates);
          } else {
            console.warn("Failed to fetch assigned metadata definitions:", assignedResponse.status);
            setAssignedRelationships([]);
          }
        } catch (assignError) {
          console.warn("Error fetching assigned metadata definitions:", assignError);
          setAssignedRelationships([]);
        }
        
      } catch (err) {
        console.error("Error loading data:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadData();
    }
  }, [id]);

  function isFieldAssigned(fieldId) {
    return fieldStates[fieldId]?.assigned || false;
  }

  function isFieldRequired(fieldId) {
    return fieldStates[fieldId]?.required || false;
  }

  function getRelationshipId(fieldId) {
    return fieldStates[fieldId]?.relationshipId;
  }

  const handleToggleAssign = async (field) => {
    const wasAssigned = isFieldAssigned(field.id);
    const relationshipId = getRelationshipId(field.id);
    
    setSaving(true);
    try {
      if (wasAssigned) {
        // Remove assignment using relationship ID
        const response = await DocumentTypeApi.removeMetadataDefinitionFromDocumentType(id, relationshipId);
        if (response.ok) {
          setFieldStates(prev => {
            const newStates = { ...prev };
            delete newStates[field.id]; // Remove the field state entirely
            return newStates;
          });
          // Remove from assignedRelationships
          setAssignedRelationships(prev => prev.filter(rel => rel.id !== relationshipId));
        } else {
          throw new Error(`Failed to remove assignment: ${response.status}`);
        }
      } else {
        // Add assignment
        const response = await DocumentTypeApi.assignMetadataDefinitionToDocumentType(id, {
          metadataDefinitionId: field.id,
          required: false
        });
        if (response.ok) {
          const newRelationship = await response.json();
          setFieldStates(prev => ({
            ...prev,
            [field.id]: {
              assigned: true,
              required: false,
              relationshipId: newRelationship.id
            }
          }));
          // Add to assignedRelationships
          setAssignedRelationships(prev => [...prev, newRelationship]);
        } else {
          throw new Error(`Failed to add assignment: ${response.status}`);
        }
      }
    } catch (error) {
      console.error("Error toggling assignment:", error);
      alert(`Error updating assignment: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleRequired = async (fieldId) => {
    if (!isFieldAssigned(fieldId)) return; // Can't make unassigned field required
    
    const currentRequired = isFieldRequired(fieldId);
    const relationshipId = getRelationshipId(fieldId);
    
    // Find the relationship to get the current data
    const relationship = assignedRelationships.find(rel => rel.id === relationshipId);
    if (!relationship) {
      console.error("Could not find relationship for field:", fieldId);
      return;
    }
    
    setSaving(true);
    try {
      const response = await DocumentTypeApi.updateDocumentTypeMetadataDefinition(id, relationshipId, {
        metadataDefinitionId: relationship.metadataDefinitionId,
        isRequired: !currentRequired,
        ordering: relationship.ordering
      });
      
      if (response.ok) {
        setFieldStates(prev => ({
          ...prev,
          [fieldId]: {
            ...prev[fieldId],
            required: !currentRequired
          }
        }));
        
        // Update assignedRelationships
        setAssignedRelationships(prev => prev.map(rel => 
          rel.id === relationshipId 
            ? { ...rel, isRequired: !currentRequired }
            : rel
        ));
      } else {
        const errorText = await response.text();
        throw new Error(`Failed to update required status: ${response.status} - ${errorText}`);
      }
    } catch (error) {
      console.error("Error toggling required:", error);
      alert(`Error updating required status: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return (
      <div>
        <h2>Error Loading Document Type</h2>
        <p style={{ color: 'red' }}>Error: {error}</p>
        <button onClick={() => navigate("/settings/document-types")}>Back to Document Types</button>
      </div>
    );
  }

  if (!docType) {
    return (
      <div>
        <h2>Document Type Not Found</h2>
        <button onClick={() => navigate("/settings/document-types")}>Back to Document Types</button>
      </div>
    );
  }

  return (
    <div>
      <h2>Assign Fields to Document Type: {docType.name}</h2>
      <button onClick={() => navigate("/settings/document-types")}>Back</button>
      
      {saving && (
        <div style={{ marginTop: "1rem", padding: "1rem", backgroundColor: "var(--intent-warning-bg, #fff3cd)", border: "1px solid var(--intent-warning-border, #ffeaa7)", borderRadius: "4px" }}>
          <p>Saving changes...</p>
        </div>
      )}

      {allFields.length === 0 ? (
        <div style={{ marginTop: "1rem" }}>
          <p>No metadata definitions available. You may need to create some metadata definitions first.</p>
          <button onClick={() => navigate("/settings/document-metadata-definitions")}>
            Go to Document Metadata Definitions
          </button>
        </div>
      ) : (
        <table style={{ marginTop: "1rem", borderCollapse: "collapse", width: "100%" }}>
          <thead>
            <tr>
              <th style={{ border: "1px solid #ccc", padding: "8px", textAlign: "left" }}>
                Field Name
              </th>
              <th style={{ border: "1px solid #ccc", padding: "8px", textAlign: "left" }}>
                Type
              </th>
              <th style={{ border: "1px solid #ccc", padding: "8px", textAlign: "left" }}>
                Description
              </th>
              <th style={{ border: "1px solid #ccc", padding: "8px", textAlign: "center" }}>
                Assigned?
              </th>
              <th style={{ border: "1px solid #ccc", padding: "8px", textAlign: "center" }}>
                Required?
              </th>
            </tr>
          </thead>
          <tbody>
            {allFields.map((field) => {
              const isAssigned = isFieldAssigned(field.id);
              const isRequired = isFieldRequired(field.id);
              
              return (
                <tr key={field.id}>
                  <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                    {field.name}
                  </td>
                  <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                    {field.field_type}
                  </td>
                  <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                    {field.description || "-"}
                  </td>
                  <td style={{ border: "1px solid #ccc", padding: "8px", textAlign: "center" }}>
                    <input
                      type="checkbox"
                      checked={isAssigned}
                      onChange={() => handleToggleAssign(field)}
                      disabled={saving}
                      title="Assign this field to the document type"
                    />
                  </td>
                  <td style={{ border: "1px solid #ccc", padding: "8px", textAlign: "center" }}>
                    {isAssigned ? (
                      <input
                        type="checkbox"
                        checked={isRequired}
                        onChange={() => handleToggleRequired(field.id)}
                        disabled={saving}
                        title="Make this field required"
                      />
                    ) : (
                      <span style={{ color: "#999" }}>-</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <div style={{ marginTop: "2rem" }}>
        <h3>Summary</h3>
        <p>
          <strong>Assigned Fields:</strong> {Object.values(fieldStates).filter(s => s.assigned).length} of {allFields.length}
        </p>
        <p>
          <strong>Required Fields:</strong> {Object.values(fieldStates).filter(s => s.assigned && s.required).length}
        </p>
        
        <h3>Current Assignments</h3>
        {assignedRelationships.length > 0 ? (
          <ul>
            {assignedRelationships.map(rel => (
              <li key={rel.id}>
                <strong>{rel.metadataName}</strong> (ID: {rel.metadataDefinitionId}, Relationship ID: {rel.id})
                {rel.isRequired && <span style={{ color: 'red' }}> - Required</span>}
              </li>
            ))}
          </ul>
        ) : (
          <p>No fields assigned to this document type.</p>
        )}
        
        <h3>Available Actions</h3>
        <ul>
          <li>
            <button onClick={() => navigate("/settings/document-metadata-definitions")}>
              Manage Document Metadata Definitions
            </button>
          </li>
          <li>
            <button onClick={() => navigate("/settings/document-types")}>
              Back to Document Types
            </button>
          </li>
        </ul>
      </div>
    </div>
  );
}

export default AssignFieldsToDocumentType;
